import React from 'react';
import clsx from 'clsx';
import { SpinnerIcon } from './SpinnerIcon';
import Tooltip from './Tooltip';

const VARIANT_STYLES = {
  primary: 'bg-emerald-600 text-white hover:bg-emerald-700',
  secondary: 'bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700',
  danger: 'bg-red-600 dark:bg-red-400 text-white dark:text-black hover:bg-red-700 dark:hover:bg-red-300',
  outline: 'border border-gray-400 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-900',
};

const SIZE_STYLES = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-base',
};

const ICON_ONLY_SIZE = {
  sm: 'w-8 h-8 p-1',
  md: 'w-10 h-10 p-2',
  lg: 'w-12 h-12 p-3',
};

const Button = ({
  children,
  icon: Icon,
  className = '',
  disabled = false,
  readOnly = false,
  type = 'button',
  id,
  href,
  onClick,
  variant = 'primary',
  theme = '',
  size = 'md',
  fullWidth = false,
  iconOnly = false,
  isLoading = false,
  loadingText,
  tooltip,
  tooltipPlacement = 'top',
  ...props
}) => {
  const baseStyles =
    'inline-flex items-center justify-center font-normal rounded-md transition duration-200 focus:outline-none';

  const finalClass = clsx(
    baseStyles,
    iconOnly ? ICON_ONLY_SIZE[size] : SIZE_STYLES[size],
    VARIANT_STYLES[variant],
    fullWidth && !iconOnly && 'w-full block',
    (disabled || isLoading) && 'opacity-70 cursor-not-allowed',
    className
  );

  const handleClick = (e) => {
    if (disabled || readOnly || isLoading) {
      e.preventDefault();
      return;
    }
    if (typeof onClick === 'function') {
      onClick(e);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return <>
        <SpinnerIcon className="h-5 w-5 animate-spin text-white dark:text-black mr-2" />
        {!iconOnly && (loadingText || 'Loading...')}
      </>;
    }

    return (
      <>
        {Icon && <Icon className={`h-5 w-5 ${!iconOnly ? 'mr-2' : ''}`} />}
        {!iconOnly && children}
      </>
    );
  };

  if (href) {
    return (
        <Tooltip content={tooltip} placement={tooltipPlacement}>
            <a
                id={id}
                href={href}
                className={finalClass}
                onClick={handleClick}
                {...props}
            >
                {renderContent()}
            </a>
        </Tooltip>
    );
  }

  return (
    <Tooltip content={tooltip} placement={tooltipPlacement}>
        <button
        id={id}
        type={type}
        className={finalClass}
        disabled={disabled || isLoading}
        readOnly={readOnly}
        onClick={handleClick}
        {...props}
        >
        {renderContent()}
        </button>
    </Tooltip>
  );
};

export default Button;
